import React, { useState, useEffect } from 'react'
import Layout from '../../../components/vision/Layout'
import ProtectedRoute from '../../../components/vision/ProtectedRoute'
import { supabase } from '../../../lib/supabase'
import type { Cliente, ClienteNota, SegmentoCliente } from '../../../lib/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import { useFieldPermissions } from '../../../hooks/useFieldPermissions'
import NotasList from '../../../components/vision/NotasList'
import NotaForm from '../../../components/vision/NotaForm'
import ClientePlanos from '../../../components/vision/ClientePlanos'
import ClienteContratos from '../../../components/vision/ClienteContratos'
import ModalAlterarStatus from '../../../components/vision/ModalAlterarStatus'
import { ContractModalProvider } from '../../../contexts/ContractModalContext'
import GlobalContractModal from '../../../components/vision/GlobalContractModal'
import GlobalContractEditor from '../../../components/vision/GlobalContractEditor'

const ClientesPage: React.FC = () => {
  const { user } = useAuth()
  const { canEdit, canView, shouldShowAction } = useFieldPermissions()
  const [clientes, setClientes] = useState<Cliente[]>([])
  
  // Helper para obter props de campo baseado em permissão
  const getFieldProps = (permission: string, baseClassName: string = '') => {
    const canEditField = canEdit(permission)
    return {
      className: `${baseClassName} ${!canEditField || isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`,
      readOnly: isViewMode || !canEditField,
      disabled: !canEditField
    }
  }
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'ativo' | 'inativo'>('all')
  const [selectedClient, setSelectedClient] = useState<Cliente | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [activeTab, setActiveTab] = useState('dados-pessoais')
  const [showNotaForm, setShowNotaForm] = useState(false)
  const [editingNota, setEditingNota] = useState<ClienteNota | null>(null)
  const [notasRefreshTrigger, setNotasRefreshTrigger] = useState(0)
  const [isViewMode, setIsViewMode] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [clienteParaAlterarStatus, setClienteParaAlterarStatus] = useState<Cliente | null>(null)
  const [showNewClientModal, setShowNewClientModal] = useState(false)
  const [segmentos, setSegmentos] = useState<SegmentoCliente[]>([])

  useEffect(() => {
    fetchSegmentos()
  }, [])

  const fetchSegmentos = async () => {
    try {
      const res = await fetch('/api/gestao/segmentos')
      if (res.ok) {
        const data = await res.json()
        setSegmentos(data)
      }
    } catch (error) {
      console.error('Erro ao buscar segmentos:', error)
    }
  }

  useEffect(() => {
    const fetchClientes = async () => {
      
      try {
        const response = await fetch('/api/gestao/clientes')
        
        if (!response.ok) {
          throw new Error(`Erro na API: ${response.status}`)
        }
        
        const result = await response.json()
        
        setClientes(result.data || [])
        
      } catch (error) {
        console.error('❌ Erro ao carregar clientes:', error)
        setClientes([])
      } finally {
        setLoading(false)
      }
    }

    fetchClientes()
  }, [])


  const handleStatusChange = (cliente: Cliente) => {
    setClienteParaAlterarStatus(cliente)
    setShowStatusModal(true)
  }

  const confirmarAlteracaoStatus = async (novoStatus: 'ativo' | 'inativo', dataInativacao?: string) => {
    if (!clienteParaAlterarStatus) return

    try {
      const updateData: Partial<Cliente> = {
        status: novoStatus,
        data_inativacao: dataInativacao || null
      }

      const response = await fetch(`/api/gestao/clientes?id=${clienteParaAlterarStatus.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      })

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.error) {
        throw new Error(result.error)
      }

      // Atualizar a lista local
      setClientes(prev => prev.map(client => 
        client.id === clienteParaAlterarStatus.id 
          ? { 
              ...client,
              status: novoStatus,
              data_inativacao: dataInativacao || null
            }
          : client
      ))

      setShowStatusModal(false)
      setClienteParaAlterarStatus(null)
      
    } catch (error) {
      console.error('Erro ao alterar status do cliente:', error)
      alert('Erro ao alterar status do cliente. Verifique o console para mais detalhes.')
    }
  }

  const filteredClientes = clientes.filter(client => {
    const searchLower = searchTerm.toLowerCase()
    
    const matchesSearch = 
      // Campos originais
      (client.nome_completo?.toLowerCase() || '').includes(searchLower) ||
      (client.email?.toLowerCase() || '').includes(searchLower) ||
      (client.celular || '').includes(searchTerm) ||
      (client.nome_empresa?.toLowerCase() || '').includes(searchLower) ||
      
      // Novos campos adicionados
      (client.razao_social?.toLowerCase() || '').includes(searchLower) ||
      (client.cnpj || '').includes(searchTerm) ||
      (client.telefone || '').includes(searchTerm) ||
      (client.cidade?.toLowerCase() || '').includes(searchLower) ||
      (client.responsavel_financeiro?.toLowerCase() || '').includes(searchLower)
    
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter
    
    return matchesSearch && matchesStatus
  })

  const formatDate = (dateString: string) => {
    // Parse the UTC date and format it without timezone conversion
    const date = new Date(dateString)
    // Use UTC methods to avoid timezone conversion issues
    const day = date.getUTCDate().toString().padStart(2, '0')
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
    const year = date.getUTCFullYear()
    const hours = date.getUTCHours().toString().padStart(2, '0')
    const minutes = date.getUTCMinutes().toString().padStart(2, '0')
    
    return `${day}/${month}/${year}, ${hours}:${minutes}`
  }

  const openEditModal = (client: Cliente) => {
    setSelectedClient(client)
    setIsViewMode(false)
    setShowEditModal(true)
  }

  const openViewModal = (client: Cliente) => {
    setSelectedClient(client)
    setIsViewMode(true)
    setShowEditModal(true)
  }

  const closeEditModal = () => {
    setShowEditModal(false)
    setSelectedClient(null)
    setActiveTab('dados-pessoais')
    setShowNotaForm(false)
    setEditingNota(null)
    setIsViewMode(false)
  }

  // Funções para gerenciar notas
  const handleNovaNotaClick = () => {
    setEditingNota(null)
    setShowNotaForm(true)
  }

  const handleEditNota = (nota: ClienteNota) => {
    setEditingNota(nota)
    setShowNotaForm(true)
  }

  const handleNotaSaved = (savedNota: ClienteNota) => {
    
    setShowNotaForm(false)
    setEditingNota(null)
    setNotasRefreshTrigger(prev => prev + 1)
    
    // Garantir que permanece na aba de notas e modal aberto
    setActiveTab('notas')
  }

  const handleNotaDeleted = () => {
    setNotasRefreshTrigger(prev => prev + 1)
  }

  const createNewClient = async (clientData: Partial<Cliente>) => {
    try {
      console.log('🆕 CreateClient: Criando novo cliente:', clientData)
      
      const response = await fetch('/api/gestao/clientes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(clientData)
      })

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.error) {
        throw new Error(result.error)
      }

      // Adicionar novo cliente à lista
      if (result.data) {
        setClientes(prev => [result.data, ...prev])
        alert('✅ Cliente cadastrado com sucesso!')
        setShowNewClientModal(false)
      }
    } catch (error) {
      console.error('❌ Erro ao criar cliente:', error)
      alert('Erro ao cadastrar cliente. Verifique o console para mais detalhes.')
    }
  }

  const deleteClient = async () => {
    if (!selectedClient) return

    // Confirmação dupla para evitar exclusões acidentais
    const confirmMessage = `⚠️ ATENÇÃO: Você está prestes a EXCLUIR permanentemente o cliente:\n\n"${selectedClient.nome_completo}"\n\nEsta ação NÃO pode ser desfeita!\n\nTodos os dados relacionados (notas, contratos, planos) serão perdidos.\n\nDeseja realmente continuar?`
    
    if (!confirm(confirmMessage)) {
      return
    }

    // Segunda confirmação
    const secondConfirm = confirm('Confirme novamente: Deseja EXCLUIR este cliente permanentemente?')
    if (!secondConfirm) {
      return
    }

    try {
      console.log('🗑️ DeleteClient: Excluindo cliente:', selectedClient.id)
      
      const response = await fetch(`/api/gestao/clientes?id=${selectedClient.id}`, {
        method: 'DELETE'
      })

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.error) {
        throw new Error(result.error)
      }

      // Remover cliente da lista local
      setClientes(prev => prev.filter(client => client.id !== selectedClient.id))

      // Fechar modal
      closeEditModal()

      // Mostrar mensagem de sucesso
      alert('✅ Cliente excluído com sucesso!')

    } catch (error) {
      console.error('❌ Erro ao excluir cliente:', error)
      alert('Erro ao excluir cliente. Verifique o console para mais detalhes.')
    }
  }

  const updateClient = async (updatedData: Partial<Cliente>) => {
    if (!selectedClient) return

    try {
      console.log('🔄 UpdateClient: Dados sendo enviados:', updatedData)
      console.log('🔄 data_cadastro específico:', updatedData.data_cadastro)
      
      const response = await fetch(`/api/gestao/clientes?id=${selectedClient.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedData)
      })

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.error) {
        throw new Error(result.error)
      }


      // Criar objeto atualizado do cliente
      const clienteAtualizado = { 
        ...selectedClient,    // Manter TODOS os dados originais primeiro
        ...updatedData,       // Aplicar APENAS as mudanças do formulário
        // Garantir que campos obrigatórios não sejam null
        nome_completo: updatedData.nome_completo || selectedClient.nome_completo || '',
        celular: updatedData.celular || selectedClient.celular || '',
        status: updatedData.status || selectedClient.status || 'ativo'
      }

      // Atualizar a lista local
      setClientes(prev => prev.map(client => 
        client.id === selectedClient.id ? clienteAtualizado : client
      ))

      // Atualizar o selectedClient para manter os dados no formulário
      setSelectedClient(clienteAtualizado)

      // Mostrar mensagem de sucesso
      alert('✅ Dados salvos com sucesso!')

      // NÃO fechar o modal - permitir que o usuário continue editando
    } catch (error) {
      console.error('❌ Erro ao atualizar cliente:', error)
      alert('Erro ao salvar alterações. Verifique o console para mais detalhes.')
    }
  }

  if (loading) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">Carregando clientes...</p>
            </div>
          </div>
        </Layout>
      </ProtectedRoute>
    )
  }

  return (
    <ProtectedRoute>
      <Layout>
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold gradient-text-orange mb-2">Gestão de Clientes</h1>
                <p className="text-gray-600">Visualize e gerencie todos os clientes cadastrados</p>
              </div>
              {shouldShowAction('clientes.criar') && (
                <button
                  onClick={() => setShowNewClientModal(true)}
                  className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-amber-600 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Cadastro de Clientes</span>
                </button>
              )}
            </div>

            {/* Filtros */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-orange-100">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Busca */}
                <div className="flex-1">
                  <div className="relative">
                    <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                    <input
                      type="text"
                      placeholder="Buscar por nome, email, telefone, CNPJ, empresa, razão social, cidade..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300"
                    />
                  </div>
                </div>

                {/* Filtro de Status */}
                <div className="md:w-48">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as 'all' | 'ativo' | 'inativo')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300"
                  >
                    <option value="all">Todos os Status</option>
                    <option value="ativo">Apenas Ativos</option>
                    <option value="inativo">Apenas Inativos</option>
                  </select>
                </div>
              </div>

              {/* Estatísticas */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold gradient-text-orange">{filteredClientes.length}</p>
                    <p className="text-sm text-gray-600">Clientes Encontrados</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{filteredClientes.filter(c => c.status === 'ativo').length}</p>
                    <p className="text-sm text-gray-600">Ativos</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">{filteredClientes.filter(c => c.status === 'inativo').length}</p>
                    <p className="text-sm text-gray-600">Inativos</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de Clientes */}
            <div className="bg-white rounded-2xl shadow-lg border border-orange-100 overflow-hidden">
              {filteredClientes.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-orange-50 to-red-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Empresa</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Contato</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Cliente</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Data Cadastro</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredClientes.map((client) => (
                        <tr key={client.id} className="hover:bg-orange-50 hover:bg-opacity-50 transition-colors duration-200">
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm text-gray-900">{client.nome_empresa || '-'}</p>
                              <p className="text-sm text-gray-600">{client.razao_social || '-'}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm text-gray-900">{client.email || '-'}</p>
                              <p className="text-sm text-gray-600">{client.telefone || '-'}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-semibold text-gray-900">{client.nome_completo}</p>
                              <p className="text-sm text-gray-600">{client.celular}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-900">{formatDate(client.data_cadastro)}</p>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleStatusChange(client)}
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${
                                client.status === 'ativo'
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                  : 'bg-red-100 text-red-800 hover:bg-red-200'
                              }`}
                            >
                              <div className={`w-2 h-2 rounded-full mr-2 ${
                                client.status === 'ativo' ? 'bg-green-500' : 'bg-red-500'
                              }`}></div>
                              {client.status === 'ativo' ? 'Ativo' : 'Inativo'}
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex space-x-3">
                              <button
                                onClick={() => openViewModal(client)}
                                className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors duration-200 flex items-center space-x-1"
                                title="Visualizar cliente"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span>Visualizar</span>
                              </button>
                              {shouldShowAction('clientes.editar') && (
                                <button
                                  onClick={() => openEditModal(client)}
                                  className="text-orange-600 hover:text-orange-800 font-medium text-sm transition-colors duration-200 flex items-center space-x-1"
                                  title="Editar cliente"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  <span>Editar</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="text-gray-500 text-lg mb-2">Nenhum cliente encontrado</p>
                  <p className="text-gray-400">Tente ajustar os filtros de busca</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal de Edição */}
        {showEditModal && selectedClient && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={(e) => {
              // PREVENT modal from closing on overlay click
              e.stopPropagation()
              e.preventDefault()
            }}
          >
            <div 
              className="bg-white rounded-3xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden border border-gray-100"
              onClick={(e) => {
                // PREVENT clicks inside modal from propagating to overlay
                e.stopPropagation()
              }}
            >
              {/* Header Sofisticado */}
              <div className="bg-gradient-to-r from-orange-50 via-amber-25 to-orange-50 border-b border-orange-100 px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                        {isViewMode ? 'Visualizar Cliente' : 'Editar Cliente'}
                      </h2>
                      <p className="text-orange-700 text-sm font-medium mb-3">
                        {isViewMode ? 'Visualize os dados do cliente' : 'Altere os dados do cliente conforme necessário'}
                      </p>
                      
                      {/* Campo Nome da Empresa - Sempre Visível */}
                      <div className="mt-4">
                        <label className="block text-xs font-semibold text-orange-800 mb-1 flex items-center space-x-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span>Nome da Empresa</span>
                        </label>
                        <input
                          type="text"
                          name="nome_empresa_header"
                          defaultValue={selectedClient.nome_empresa || ''}
                          onChange={(e) => {
                            // Atualizar o selectedClient para manter sincronizado
                            setSelectedClient(prev => prev ? {...prev, nome_empresa: e.target.value} : null)
                          }}
                          className={`w-full px-4 py-2.5 bg-white border-2 border-orange-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 shadow-sm transition-all duration-200 text-gray-900 placeholder-gray-400 ${
                            isViewMode ? 'bg-gray-50 cursor-not-allowed' : ''
                          }`}
                          placeholder="Digite o nome da empresa..."
                          readOnly={isViewMode}
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      setShowEditModal(false)
                    }}
                    className="p-2 hover:bg-orange-100 rounded-xl transition-colors duration-200"
                  >
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="flex h-full">
                {/* Sidebar de Navegação */}
                <div className="w-80 bg-gradient-to-b from-gray-50 to-gray-100 border-r border-gray-200 p-6">
                  <nav className="space-y-2">
                    {[
                      { id: 'dados-pessoais', label: 'Dados Pessoais', icon: '👤', color: 'blue', permission: 'clientes.dados_pessoais.visualizar' },
                      { id: 'dados-empresa', label: 'Dados da Empresa', icon: '🏢', color: 'emerald', permission: 'clientes.dados_empresa.visualizar' },
                      { id: 'endereco', label: 'Endereço', icon: '📍', color: 'red', permission: 'clientes.endereco.visualizar' },
                      { id: 'marketing', label: 'Marketing Digital', icon: '📱', color: 'purple', permission: 'clientes.marketing.visualizar' },
                      { id: 'dados-anuncios', label: 'Dados para Anúncios', icon: '🎯', color: 'orange', permission: 'clientes.dados_anuncios.visualizar' },
                      { id: 'financeiro', label: 'Responsável Financeiro', icon: '💰', color: 'green', permission: 'clientes.financeiro.visualizar' },
                      { id: 'planos', label: 'Planos', icon: '📊', color: 'indigo', permission: 'clientes.planos.visualizar' },
                      { id: 'contratos', label: 'Contratos', icon: '📄', color: 'teal', permission: 'contratos.visualizar' },
                      { id: 'observacoes', label: 'Observações', icon: '📝', color: 'yellow', permission: 'clientes.observacoes.visualizar' },
                      { id: 'notas', label: 'Notas', icon: '📋', color: 'pink', permission: 'clientes.notas.visualizar' }
                    ].map(tab => {
                      // Ocultar aba se não tem permissão de visualização
                      if (tab.permission && !canView(tab.permission)) {
                        return null
                      }
                      
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => setActiveTab(tab.id)}
                          className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                            activeTab === tab.id
                              ? `bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg transform scale-105`
                              : `text-gray-700 hover:bg-white hover:shadow-md hover:scale-102`
                          }`}
                        >
                          <span className="text-lg">{tab.icon}</span>
                          <span className="font-semibold">{tab.label}</span>
                          {activeTab === tab.id && (
                            <div className="ml-auto">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </button>
                      )
                    })}
                  </nav>
                </div>

                {/* Conteúdo Principal */}
                <div className="flex-1 overflow-y-auto">
                  <div className="p-8">

                <form onSubmit={(e) => {
                  e.preventDefault()
                  const formData = new FormData(e.target as HTMLFormElement)
                  const updatedData = {
                    nome_completo: formData.get('nome_completo') as string,
                    celular: formData.get('celular') as string,
                    email: formData.get('email') as string,
                    telefone: formData.get('telefone') as string,
                    // Priorizar o campo do cabeçalho se existir, senão usar o da aba
                    nome_empresa: formData.get('nome_empresa_header') as string || formData.get('nome_empresa') as string,
                    razao_social: formData.get('razao_social') as string,
                    data_inauguracao: formData.get('data_inauguracao') as string,
                    cnpj: formData.get('cnpj') as string,
                    segmento_id: formData.get('segmento_id') as string || null,
                    inscricao_estadual: formData.get('inscricao_estadual') as string,
                    inscricao_municipal: formData.get('inscricao_municipal') as string,
                    telefone_secundario: formData.get('telefone_secundario') as string,
                    capital_social: formData.get('capital_social') as string,
                    quantos_colaboradores: formData.get('quantos_colaboradores') as string,
                    faturamento_empresa: formData.get('faturamento_empresa') as string,
                    faturamento_desejado: formData.get('faturamento_desejado') as string,
                    endereco: formData.get('endereco') as string,
                    cep: formData.get('cep') as string,
                    bairro: formData.get('bairro') as string,
                    cidade: formData.get('cidade') as string,
                    uf: formData.get('uf') as string,
                    site: formData.get('site') as string,
                    instagram: formData.get('instagram') as string,
                    facebook: formData.get('facebook') as string,
                    whatsapp_anuncios: formData.get('whatsapp_anuncios') as string,
                    cidades_anunciar: formData.get('cidades_anunciar') as string,
                    bairros_anunciar: formData.get('bairros_anunciar') as string,
                    publico_ideal: formData.get('publico_ideal') as string,
                    informacoes_adicionais_anuncios: formData.get('informacoes_adicionais_anuncios') as string,
                    responsavel_financeiro: formData.get('responsavel_financeiro') as string,
                    email_financeiro: formData.get('email_financeiro') as string,
                    telefone_financeiro: formData.get('telefone_financeiro') as string,
                    dia_nascimento: formData.get('dia_nascimento') as string,
                    mes_nascimento: formData.get('mes_nascimento') as string,
                    ano_nascimento: formData.get('ano_nascimento') as string,
                    observacoes: formData.get('observacoes') as string,
                    data_cadastro: formData.get('data_cadastro') as string,
                  }
                  updateClient(updatedData)
                }} className="space-y-6">
                  {/* Aba: Dados Pessoais */}
                  {activeTab === 'dados-pessoais' && (
                    <div className="space-y-8">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                        <div className="flex items-center space-x-3 mb-6">
                          <div className="p-2 bg-blue-500 rounded-xl">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-blue-900">Informações Pessoais</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-blue-800 mb-2">Nome Completo *</label>
                            <input
                              type="text"
                              name="nome_completo"
                              defaultValue={selectedClient.nome_completo}
                              className={`w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-200 ${
                                isViewMode ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                              }`}
                              required
                              readOnly={isViewMode}
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-blue-800 mb-2">Celular *</label>
                            <input
                              type="tel"
                              name="celular"
                              defaultValue={selectedClient.celular}
                              className={`w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-200 ${
                                isViewMode ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                              }`}
                              required
                              readOnly={isViewMode}
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-blue-800 mb-2">Email</label>
                            <input
                              type="email"
                              name="email"
                              defaultValue={selectedClient.email || ''}
                              className={`w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-200 ${
                                isViewMode ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                              }`}
                              readOnly={isViewMode}
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-blue-800 mb-2">Telefone</label>
                            <input
                              type="tel"
                              name="telefone"
                              defaultValue={selectedClient.telefone || ''}
                              className={`w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-200 ${
                                isViewMode ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                              }`}
                              readOnly={isViewMode}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Aba: Dados da Empresa */}
                  {activeTab === 'dados-empresa' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nome da Empresa</label>
                        <input
                          type="text"
                          name="nome_empresa"
                          defaultValue={selectedClient.nome_empresa || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Razão Social</label>
                        <input
                          type="text"
                          name="razao_social"
                          defaultValue={selectedClient.razao_social || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CNPJ</label>
                        <input
                          type="text"
                          name="cnpj"
                          defaultValue={selectedClient.cnpj || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Segmento/Tipo de Negócio</label>
                        <select
                          name="segmento_id"
                          defaultValue={selectedClient.segmento_id || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          disabled={isViewMode}
                        >
                          <option value="">Selecione o segmento</option>
                          {segmentos.map(seg => (
                            <option key={seg.id} value={seg.id}>
                              {seg.icone} {seg.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Data de Inauguração</label>
                        <input
                          type="date"
                          name="data_inauguracao"
                          defaultValue={selectedClient.data_inauguracao || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Data de Cadastro</label>
                        <input
                          type="date"
                          name="data_cadastro"
                          defaultValue={selectedClient.data_cadastro ? 
                            new Date(selectedClient.data_cadastro).toISOString().split('T')[0] : ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Quantos Colaboradores</label>
                        <input
                          type="text"
                          name="quantos_colaboradores"
                          defaultValue={selectedClient.quantos_colaboradores || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Faturamento da Empresa</label>
                        <input
                          type="text"
                          name="faturamento_empresa"
                          defaultValue={selectedClient.faturamento_empresa || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Faturamento Desejado</label>
                        <input
                          type="text"
                          name="faturamento_desejado"
                          defaultValue={selectedClient.faturamento_desejado || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                    </div>
                  )}

                  {/* Aba: Endereço */}
                  {activeTab === 'endereco' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Endereço</label>
                        <input
                          type="text"
                          name="endereco"
                          defaultValue={selectedClient.endereco || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CEP</label>
                        <input
                          type="text"
                          name="cep"
                          defaultValue={(selectedClient as any).cep || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bairro</label>
                        <input
                          type="text"
                          name="bairro"
                          defaultValue={(selectedClient as any).bairro || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cidade</label>
                        <input
                          type="text"
                          name="cidade"
                          defaultValue={(selectedClient as any).cidade || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">UF</label>
                        <input
                          type="text"
                          name="uf"
                          defaultValue={(selectedClient as any).uf || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                    </div>
                  )}

                  {/* Aba: Marketing Digital */}
                  {activeTab === 'marketing' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Site</label>
                        <input
                          type="url"
                          name="site"
                          defaultValue={selectedClient.site || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            !canEdit('clientes.marketing.editar') ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          placeholder="https://exemplo.com.br"
                          readOnly={!canEdit('clientes.marketing.editar')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Instagram</label>
                        <input
                          type="text"
                          name="instagram"
                          defaultValue={selectedClient.instagram || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            !canEdit('clientes.marketing.editar') ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          placeholder="@usuario"
                          readOnly={!canEdit('clientes.marketing.editar')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Facebook</label>
                        <input
                          type="text"
                          name="facebook"
                          defaultValue={selectedClient.facebook || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            !canEdit('clientes.marketing.editar') ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          placeholder="facebook.com/pagina"
                          readOnly={!canEdit('clientes.marketing.editar')}
                        />
                      </div>
                    </div>
                  )}

                  {/* Aba: Dados para Anúncios */}
                  {activeTab === 'dados-anuncios' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp para Anúncios</label>
                        <input
                          type="tel"
                          name="whatsapp_anuncios"
                          defaultValue={selectedClient.whatsapp_anuncios || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            !canEdit('clientes.dados_anuncios.editar') ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          placeholder="(11) 99999-9999"
                          readOnly={!canEdit('clientes.dados_anuncios.editar')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cidades para Anunciar</label>
                        <input
                          type="text"
                          name="cidades_anunciar"
                          defaultValue={selectedClient.cidades_anunciar || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            !canEdit('clientes.dados_anuncios.editar') ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          placeholder="São Paulo, São Bernardo, Santo André"
                          readOnly={!canEdit('clientes.dados_anuncios.editar')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bairros para Anunciar</label>
                        <input
                          type="text"
                          name="bairros_anunciar"
                          defaultValue={selectedClient.bairros_anunciar || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            !canEdit('clientes.dados_anuncios.editar') ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          placeholder="Centro, Jardim do Mar, Rudge Ramos"
                          readOnly={!canEdit('clientes.dados_anuncios.editar')}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Público Ideal</label>
                        <textarea
                          name="publico_ideal"
                          defaultValue={selectedClient.publico_ideal || ''}
                          rows={4}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            !canEdit('clientes.dados_anuncios.editar') ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          placeholder="Descreva o público-alvo ideal para os anúncios: idade, gênero, interesses, comportamentos, etc."
                          readOnly={!canEdit('clientes.dados_anuncios.editar')}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Informações adicionais para Anúncios</label>
                        <textarea
                          name="informacoes_adicionais_anuncios"
                          defaultValue={selectedClient.informacoes_adicionais_anuncios || ''}
                          rows={5}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            !canEdit('clientes.dados_anuncios.editar') ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          placeholder="Insira informações importantes levantadas na reunião de Onboard junto a este cliente"
                          readOnly={!canEdit('clientes.dados_anuncios.editar')}
                        />
                      </div>
                    </div>
                  )}

                  {/* Aba: Responsável Financeiro */}
                  {activeTab === 'financeiro' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Responsável Financeiro</label>
                        <input
                          type="text"
                          name="responsavel_financeiro"
                          defaultValue={selectedClient.responsavel_financeiro || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Financeiro</label>
                        <input
                          type="email"
                          name="email_financeiro"
                          defaultValue={selectedClient.email_financeiro || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Telefone Financeiro</label>
                        <input
                          type="tel"
                          name="telefone_financeiro"
                          defaultValue={selectedClient.telefone_financeiro || ''}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          readOnly={isViewMode}
                        />
                      </div>
                    </div>
                  )}

                  {/* Aba: Observações */}
                  {activeTab === 'observacoes' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Observações</label>
                        <textarea
                          name="observacoes"
                          defaultValue={selectedClient.observacoes || ''}
                          rows={6}
                          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                            !canEdit('clientes.observacoes.editar') ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          placeholder="Digite aqui observações importantes sobre o cliente..."
                          readOnly={!canEdit('clientes.observacoes.editar')}
                        />
                      </div>
                    </div>
                  )}

                  {/* Aba: Planos */}
                  {activeTab === 'planos' && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">Planos do Cliente</h3>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex items-start">
                            <div className="text-blue-600 mr-2 mt-0.5">ℹ️</div>
                            <div>
                              <p className="text-sm text-blue-800">
                                <strong>Para criar novas vendas:</strong> Acesse o menu 
                                <span className="font-mono bg-blue-100 px-1 rounded ml-1">Planos → Nova Venda</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <ClientePlanos clienteId={selectedClient.id} />
                    </div>
                  )}

                  {/* Aba: Contratos */}
                  {activeTab === 'contratos' && (
                    <div className="space-y-6">
                      <ClienteContratos clienteId={selectedClient.id} />
                    </div>
                  )}

                  {/* Aba: Notas */}
                  {activeTab === 'notas' && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">Notas do Cliente</h3>
                        {shouldShowAction('clientes.notas.criar') && (
                          <button
                            type="button"
                            onClick={handleNovaNotaClick}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                          >
                            + Nova Nota
                          </button>
                        )}
                      </div>
                      
                      <NotasList
                        clienteId={selectedClient.id}
                        onEditNota={handleEditNota}
                        refreshTrigger={notasRefreshTrigger}
                      />
                    </div>
                  )}

                  {/* Botões do Modal */}
                  {activeTab !== 'notas' && activeTab !== 'contratos' && (
                    <div className="flex justify-between items-center pt-8 mt-8 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white rounded-b-2xl px-8 py-6">
                      {/* Botão Excluir à esquerda */}
                      <div>
                        {!isViewMode && shouldShowAction('clientes.excluir') && (
                          <button
                            type="button"
                            onClick={deleteClient}
                            className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span>Excluir Cliente</span>
                          </button>
                        )}
                      </div>

                      {/* Botões Cancelar e Salvar à direita */}
                      <div className="flex space-x-4">
                        <button
                          type="button"
                          onClick={closeEditModal}
                          className="px-8 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 hover:border-gray-400 font-semibold transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          {isViewMode ? 'Fechar' : 'Cancelar'}
                        </button>
                        {(!isViewMode || canEdit('clientes.marketing.editar') || canEdit('clientes.dados_anuncios.editar') || canEdit('clientes.observacoes.editar')) && (
                          <button
                            type="submit"
                            className="px-8 py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                          >
                            Salvar Alterações
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </form>

                {/* Botão Fechar para aba de notas e contratos */}
                {(activeTab === 'notas' || activeTab === 'contratos') && (
                  <div className="flex justify-end pt-8 mt-8 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white rounded-b-2xl px-8 py-6">
                    <button
                      type="button"
                      onClick={closeEditModal}
                      className="px-8 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 hover:border-gray-400 font-semibold transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      Fechar
                    </button>
                  </div>
                )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Modal de Nota */}
      {showNotaForm && selectedClient && (
        <NotaForm
          clienteId={selectedClient.id}
          nota={editingNota}
          onSave={handleNotaSaved}
          onCancel={() => setShowNotaForm(false)}
        />
      )}

      {/* Modal de Alteração de Status */}
      {showStatusModal && clienteParaAlterarStatus && (
        <ModalAlterarStatus
          cliente={clienteParaAlterarStatus}
          isOpen={showStatusModal}
          onClose={() => {
            setShowStatusModal(false)
            setClienteParaAlterarStatus(null)
          }}
          onConfirm={confirmarAlteracaoStatus}
        />
      )}
      
      {/* Modal de Cadastro de Novo Cliente */}
      {showNewClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-xl">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold">Cadastro de Novo Cliente</h2>
              </div>
              <button
                onClick={() => setShowNewClientModal(false)}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault()
              const formData = new FormData(e.target as HTMLFormElement)
              const newClientData = {
                nome_completo: formData.get('nome_completo') as string,
                celular: formData.get('celular') as string,
                email: formData.get('email') as string,
                nome_empresa: formData.get('nome_empresa') as string,
                status: 'ativo' as const
              }
              createNewClient(newClientData)
            }} className="p-6 space-y-6">
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">Dados Essenciais</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      name="nome_completo"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="João da Silva"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Celular *
                    </label>
                    <input
                      type="tel"
                      name="celular"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="(11) 99999-9999"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="joao@exemplo.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Fantasia
                    </label>
                    <input
                      type="text"
                      name="nome_empresa"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Empresa Ltda"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-yellow-800">
                    Após o cadastro inicial, você poderá completar todas as informações do cliente editando seu perfil.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowNewClientModal(false)}
                  className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-semibold transition-all duration-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Cadastrar Cliente
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Global Contract Modal - Rendered at top level */}
      <GlobalContractModal />
      
      {/* Global Contract Editor - Rendered at top level */}
      <GlobalContractEditor />
      </Layout>
    </ProtectedRoute>
  )
}

const ClientesPageWithProvider: React.FC = () => {
  return (
    <ContractModalProvider>
      <ClientesPage />
    </ContractModalProvider>
  )
}

export default ClientesPageWithProvider
